// frontend/src/admin/v2/AuditFeed.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Stack, Box, Typography, Alert, Skeleton } from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import EmailIcon from '@mui/icons-material/Email'
import LinkIcon from '@mui/icons-material/Link'
import TuneIcon from '@mui/icons-material/Tune'
import { useAudit } from './hooks/useAudit'
import { SP, FS } from '../../theme/tokens'
import Panel from '../../components/common/Panel'
import ListToolbar from '../../components/common/ListToolbar'
import Pill from '../../components/common/Pill'
import TableFooterSimple from '../../components/common/TableFooterSimple'
import EmptyState from '../../components/common/EmptyState'
import RefreshButton from '../../components/common/RefreshButton'
import useListQuery from '../../components/common/listQuery/useListQuery'
import { formatBerlinDate } from '../../utils/time'

/**
 * AuditFeed — /admin/audit page.
 *
 * @description
 *   Unified chronological feed across sync runs, Shopify webhooks,
 *   notification emails and setting changes, with the list anatomy of
 *   `tabellen-filter-suche.md` §2.1: search, the „Filters" sheet, the counter
 *   and refresh in Z1, the active-filter chips in Z3, a paginated row feed
 *   below. A row feed on BOTH widths (there is no table here — §2.1 lists
 *   `liste-karte`, not `liste-tabelle`).
 *
 *   🔴 **The filter form is the SHEET, not an inline pill group** (BS6, plan
 *   §2.7). Until G14a-3 two `ToggleButtonGroup`s and a `Select` sat in the bar
 *   — P36's defect (a segment as a quick filter) and, on the phone, three
 *   rows of controls above the feed. Source and Status are `multi`
 *   dimensions (D-2: a dimension with enumerable values, not a named
 *   condition), Time is a `single` one.
 *
 *   🔴 **Time carries NO view default** (D-1). `hours` is a `single`
 *   dimension without `default`: absent from the URL, the SERVER default (24 h)
 *   applies, and the sheet names the effective window through `emptyLabel`
 *   („Last 24 hours"). A `default` would make the ✕ write `?hours=all`, which
 *   the endpoint does not know — a chip whose removal WIDENS the window is
 *   exactly what N4.2 argues against. As soon as the user picks, the chip
 *   stands in Z3.
 *
 *   🔴 **The counter names the CAP, not a guess** (plan §2.7, B3). The server
 *   caps EACH source at 1000 rows (`audit.py`, `LIMIT :hard_cap`), and `total`
 *   is the sum of the capped sources. So the counter switches to
 *   `unknownTotal` („50+ events") as soon as `total >= 1000` — a LOWER bound,
 *   never equality: in the measured default window the total is 1 260
 *   (sync capped 1 000 + webhook 250 + email 10 + settings 0), where
 *   `total === 1000` would never fire and the surface would claim an exact
 *   „50 of 1260".
 *
 *   🔴 **The old sentence „N events · showing X from offset Y" is gone.** It
 *   printed the raw offset — a pagination implementation detail — and a total
 *   the endpoint had already capped.
 *
 * @useWhen
 *   - Answering „what happened in the last N hours?" across all four sources
 *   - Tracing one webhook / one email / one setting change by name
 *
 * @related
 *   - useAudit — the fetch (aborts in flight on filter change)
 *   - CronFailures — the sibling list (same anatomy, a table)
 */
const SEITE = 50

const SOURCE_ICONS = {
  sync:     SyncIcon,
  webhook:  LinkIcon,
  email:    EmailIcon,
  settings: TuneIcon,
}

/**
 * Der Ton EINES Status — die EINE Quelle für Zeile und Blatt (R16 B1/W4).
 *
 * 🔴 `tone` ist die `Pill`-Achse (`Pill.jsx:62`: `plain|ok|warn|err|info|agent|
 * brand`), `farbe` nur noch das Symbol. Der Wortlaut kommt NICHT mehr von hier,
 * sondern aus `STATUS` — bis G14a-3-NB3 druckte die Zeile „ok"/"failed" klein,
 * das Blatt „OK"/"Failed" groß: derselbe Zustand in zwei Schreibweisen auf
 * einem Schirm.
 *
 * 🔴 `cancelled` ist `plain`, nicht `text.disabled` (R16 W2): das Statuswort
 * selbst stand auf ~2,7:1 und lag damit unter WCAG AA. `plain` ist der
 * neutrale Pill-Ton; das Symbol daneben trägt `text.secondary` (≈4,6:1).
 */
const STATUS_TON = {
  ok:        { tone: 'ok',    farbe: 'success.main' },
  failed:    { tone: 'err',   farbe: 'error.main' },
  running:   { tone: 'info',  farbe: 'info.main' },
  cancelled: { tone: 'plain', farbe: 'text.secondary' },
}

/** Die Werte der Quellen-Dimension — Wortlaut der Adresse (plan §3.1). */
const QUELLEN = [
  { value: 'sync', label: 'Sync' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'email', label: 'Email' },
  { value: 'settings', label: 'Settings' },
]

/** Die Werte der Status-Dimension (plan §3.1). */
const STATUS = [
  { value: 'ok', label: 'OK' },
  { value: 'failed', label: 'Failed' },
  { value: 'running', label: 'Running' },
  { value: 'cancelled', label: 'Cancelled' },
]

/**
 * Die Zeitfenster — Wert ist der `hours`-Parameter des Endpunkts.
 *
 * 🔴 Die Liste ist die des Plans (§2.7): 1/6/24/72/168/720. Der Server klemmt
 * auf 1…720 (`audit.py`), die 720 ist damit die Obergrenze und keine
 * geratene Zahl.
 */
const ZEITEN = [
  { value: '1', label: 'Last hour' },
  { value: '6', label: 'Last 6 hours' },
  { value: '24', label: 'Last 24 hours' },
  { value: '72', label: 'Last 3 days' },
  { value: '168', label: 'Last 7 days' },
  { value: '720', label: 'Last 30 days' },
]

/** Die Kappe JE QUELLE im Endpunkt — ab hier ist der Bestand unbekannt. */
const QUELLEN_KAPPE = 1000

const STRICH = '—'

function relTime(iso) {
  if (!iso) return STRICH
  const d = new Date(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return formatBerlinDate(d)
}

/**
 * Eine Ereigniszeile.
 *
 * 🔴 Die zwei Messmarken sind die der Hausform (`karte-kennung-` am Kasten,
 * `karte-status-` NUR um die Statusangabe): P20 zählt die Zeilen, P22 prüft,
 * dass der Status nicht über Farbe allein steht. Ohne die Statusmarke müsste
 * P22 eine Spalte im Tabellenkopf suchen — die es hier nicht gibt.
 */
function EventRow({ event }) {
  const Icon = SOURCE_ICONS[event.source] || SyncIcon
  const status = STATUS.find((s) => s.value === event.status) || STATUS[0]
  const ton = STATUS_TON[status.value] || STATUS_TON.ok
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={SP.tight}
      data-testid={`karte-kennung-${event.id}`}
      // 🔴 KEIN `hoverOnly` (R16 W5): die Zeile hat keine Aktion. Ein
      // Hintergrundwechsel beim Überfahren verspricht einen Klick, den es nicht
      // gibt („eine Fläche, die sich als Knopf ausgibt, ist ein Knopf",
      // `pattern-library.md`).
      sx={{ py: 0.5, px: SP.tight, borderRadius: 1 }}
    >
      <Icon sx={{ fontSize: 16, color: ton.farbe, mt: 0.5, flexShrink: 0 }} />
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Stack direction="row" spacing={SP.tight} alignItems="baseline" flexWrap="wrap">
          {/* 🔴 Die Titelmarke der Hausform (`karte-titel-<id>`): P12 misst die
              Rundreise ueber die Suche und zaehlt dafür Titel + Zeilen
              (`helfer.ts`, `KARTEN_TITEL`). Ohne sie fände die Probe auf einem
              Zeilenfeed mit Suche NICHTS und meldete „der Begriff trifft keine
              Zeile" — einen Befund über die Messung. */}
          <Typography
            data-testid={`karte-titel-${event.id}`}
            sx={{ fontSize: FS.sm, fontWeight: 500 }}
          >
            {event.label}
          </Typography>
          {/* 🔴 `Pill`, nicht ein eigener Kasten (R16 B1): N9.8 verlangt „Pill
              als Chipform", Regel 22 Komponentenregel 1 „one chip component", und
              die Nachbarliste `CronFailures` traegt ihre Zustaende genau so.
              Die Messmarke reist ueber `…rest` an die Wurzel (`Pill.jsx:335`).
              Der Wortlaut ist der des Blattes (`STATUS`) — eine Quelle. */}
          <Pill tone={ton.tone} data-testid={`karte-status-${event.id}`}>
            {status.label}
          </Pill>
          <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            {event.source} · {relTime(event.timestamp)}
          </Typography>
        </Stack>
        {event.detail && (
          <Typography
            sx={{
              fontSize: FS.xs, color: 'text.secondary',
              fontFamily: event.source === 'sync' ? 'monospace' : 'inherit',
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}
          >
            {event.detail}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

export default function AuditFeed() {
  // ── Der Listen-Zustand lebt in der ADRESSE (§3.1) ─────────────────────────
  const dimensionen = useMemo(() => [
    { key: 'source', kind: 'multi', label: 'Source', values: QUELLEN },
    { key: 'status', kind: 'multi', label: 'Status', values: STATUS },
    // 🔴 D-1: `single` OHNE `default` — fehlt der Parameter, gilt die
    // SERVER-Vorgabe (24 h), und das Blatt nennt sie über `emptyLabel`.
    { key: 'hours', kind: 'single', label: 'Time', values: ZEITEN },
    { key: 'q', kind: 'single', label: 'Search' },
  ], [])
  const query = useListQuery({ dimensions: dimensionen, pageSize: SEITE })
  const { state, set, clearAll, chips } = query

  // 🔴 Die Ableitungen aus `state` in EINEM `useMemo`: `state` ist im Hook
  // inhaltsbasiert stabil, die zwei Arrays daraus sind es aber nicht — als
  // nackte Ternäre neu erzeugt, liefen `filters` und `kriterien` bei JEDEM
  // Render neu (react-hooks/exhaustive-deps, und der Abruf hinge daran).
  const { quellen, stati, stunden, begriff, seite } = useMemo(() => ({
    quellen: Array.isArray(state.source) ? state.source : [],
    stati: Array.isArray(state.status) ? state.status : [],
    stunden: state.hours ?? null,
    begriff: state.q ?? '',
    seite: Math.max(1, Number(state.page) || 1),
  }), [state])

  const filters = useMemo(() => ({
    sources: quellen,
    statuses: stati,
    hours: stunden ? Number(stunden) : 24,
    q: begriff,
    limit: SEITE,
    offset: (seite - 1) * SEITE,
  }), [quellen, stati, stunden, begriff, seite])

  const { data, loading, error, errorTitle, refetch } = useAudit(filters)

  // 🔴 Die Sperre des ERSTEN Aufbaus (N8.5) — dieselbe Bauform wie `CronFailures`:
  // nur solange noch nie etwas geladen wurde.
  const [jeGeladen, setJeGeladen] = useState(false)
  const jeGeladenRef = useRef(false)
  useEffect(() => {
    if (!loading && data && !jeGeladenRef.current) {
      jeGeladenRef.current = true
      setJeGeladen(true)
    }
  }, [loading, data])
  const sperre = !jeGeladen && error != null

  const events = data?.events || []
  const gesamt = Number.isFinite(data?.total) ? data.total : null
  /** Ab der Kappe ist `gesamt` eine UNTERE Schranke, keine Gesamtzahl (B3). */
  const kappt = gesamt != null && gesamt >= QUELLEN_KAPPE

  /**
   * Die letzte gueltige Seite — EINE Formel fuer Klemm-Effekt und Fusszeile.
   *
   * 🔴 R16-Runde 3, Hinweis 1: die Fusszeile bekam die ROHE Adresse
   * (`seite - 1`), waehrend die Klemmung erst im Effekt nach dem Paint schreibt.
   * Im ersten Frame nach der Antwort stand dort bei `total 120` und `?page=4`
   * eine verdrehte Spanne („151–120 of 120"). Mit derselben Grenze wie der
   * Effekt ist der Frame richtig — und MUI's Warnung „page prop of a
   * TablePagination is out of range" faellt mit.
   */
  const letzteSeite = Math.max(1, Math.ceil((gesamt ?? 0) / SEITE))

  /*
   * 🔴 Eine Seite hinter dem Ende wird auf die LETZTE geklemmt (N10.4, R15 B1) —
   * dieselbe Bauform wie `CronFailures.jsx:245-249`. Ohne die Klemmung stuende
   * „0 of 1250 events" ueber „No events in this time range.": der zweite Satz
   * waere ueber den BESTAND falsch, denn die Ereignisse gibt es, nur nicht auf
   * dieser Seite. Erreichbar ohne Zutun: das Fenster rollt (`hours`), wer auf
   * der letzten Seite steht und spaeter „Refresh" drueckt, landet dahinter —
   * ebenso jedes alte Lesezeichen mit `?page=`.
   *
   * Die Klemmung laeuft NACH dem Laden und schreibt die Adresse (`set('page')`),
   * damit die Anzeige und die Adresse denselben Stand nennen.
   */
  useEffect(() => {
    if (gesamt == null || gesamt <= 0) return
    if (seite > letzteSeite) set('page', letzteSeite)
  }, [gesamt, seite, set, letzteSeite])

  /*
   * 🔴 Das FENSTER der Klemmung (R16-Befund 1, G14a-3 NB5).
   *
   * Nach dem ersten Abruf mit `?page=3` bei Bestand 3 haelt die Antwort
   * `events: []` und `total: 3`. Der Effekt oben schreibt `page` auf 1 — die
   * ANTWORT darauf kommt erst im naechsten Abruf. Zwischen beiden Antworten
   * stehen die ALTEN Daten (`events: []`) neben der NEUEN Seite: der
   * Leerzustand saehe „No events in this time range.“ ueber „0 of 3 events“ —
   * genau der Widerspruch, den die Klemmung beseitigen soll, nur transient
   * statt dauerhaft.
   *
   * 🔴 `seite > letzteSeite` taugt dafuer NICHT, und `loading` auch nicht: nach dem
   * Schreiben ist `seite` schon 1, waehrend `data` noch die alte Antwort traegt
   * (`useListQuery` schreibt die Adresse, der Abruf folgt im Effekt), und
   * `setLoading(true)` steht in `useAudit`, laeuft also erst NACH diesem Render.
   *
   * Der Unterscheider ist der Widerspruch selbst: `gesamt > 0` bei null Zeilen
   * und ohne Fehler. Auf einer GUELTIGEN Seite kann er nicht vorkommen —
   * `backend/admin_v2/audit.py:297` setzt `total = len(events)` ueber die volle
   * Liste und schneidet erst danach (`page = events[offset:offset + limit]`);
   * bei `offset < total` und `limit >= 1` ist die Seite also nie leer. Der
   * Zustand heisst damit: die Seite liegt hinter dem Ende, und der Abruf fuer
   * die geklemmte Seite laeuft.
   *
   * Ein FEHLER ist ausgenommen: ein gescheiterter zweiter Abruf endet im
   * Zustand `unknown`, nicht in einem endlosen Geruest.
   */
  const klemmFenster = gesamt != null && gesamt > 0 && events.length === 0 && error == null

  /**
   * Der Leerzustand — vier Gründe, je eine andere Aussage.
   *
   * 🔴 Ein gescheitertes NACHLADEN bei leerer Liste kennt den Grund NICHT
   * (`unknown`) — dieselbe Lehre wie R15-W1 auf E4: „nichts gefunden" wäre
   * eine Behauptung, „nichts da" ebenso.
   */
  const hatFilter = quellen.length > 0 || stati.length > 0 || stunden != null || begriff !== ''
  const leerGrund = sperre
    ? 'error'
    : jeGeladen && error != null && events.length === 0
      ? 'unknown'
      : jeGeladen && events.length === 0 && !klemmFenster
        ? (hatFilter ? 'no-match' : 'no-data')
        : null
  const standUnbekannt = leerGrund === 'unknown'
  /*
   * Im Klemm-Fenster ist die ZEILENZAHL unbekannt: die Antwort fuer die
   * geklemmte Seite laeuft noch. Zaehler und Blatt zeigen dort dieselbe
   * Zurueckhaltung wie im Zustand `unknown` — statt die 0 der alten Antwort zu
   * behaupten (Regel 22 §3).
   */
  const zaehlungUnbekannt = standUnbekannt || klemmFenster
  /** Das Geruest steht beim ERSTEN Aufbau UND im Klemm-Fenster. */
  const geruest = klemmFenster || (loading && !jeGeladen)

  const kriterien = useMemo(() => [
    {
      key: 'source',
      kind: 'select',
      label: 'Source',
      multiple: true,
      value: quellen,
      // 🔴 `[]` ist der LEERWERT der Dimension (kein Chip, `source` fehlt in der
      // Adresse) — nicht `null`, sonst schriebe der Hook den Marker `all`.
      onChange: (v) => set('source', Array.isArray(v) && v.length ? v : []),
      options: QUELLEN,
      // 🔴 P17: jedes Kriterium ist OHNE Öffnen lesbar (N11.14). Leer heisst
      // hier „keine Einschränkung" — das Wort steht als Leerwert, NICHT als
      // Option (P25 verbietet „All…" als OPTION, nicht als angezeigten Wert).
      emptyLabel: 'All sources',
    },
    {
      key: 'status',
      kind: 'select',
      label: 'Status',
      multiple: true,
      value: stati,
      onChange: (v) => set('status', Array.isArray(v) && v.length ? v : []),
      options: STATUS,
      emptyLabel: 'All statuses',
    },
    {
      key: 'hours',
      kind: 'select',
      label: 'Time',
      value: stunden ?? '',
      onChange: (v) => set('hours', v || null),
      options: ZEITEN,
      emptyLabel: 'Last 24 hours',
    },
  ], [quellen, stati, stunden, set])

  // N4.6: die Zahl der BLATT-Kriterien MIT Wert — nicht `chips.length` (der
  // Such-Chip ist keins davon).
  const aktiveBlattkriterien =
    (quellen.length > 0 ? 1 : 0) + (stati.length > 0 ? 1 : 0) + (stunden != null ? 1 : 0)

  const wiederholen = useCallback(() => refetch(), [refetch])

  return (
    <Stack spacing={SP.section}>
      {/* Ein gescheitertes NACH-Laden, waehrend Zeilen stehen: die Meldung, die
          der Leerzustand nicht abdeckt. Sie steht AUSSERHALB von `liste-panel` —
          P8 zaehlt dort genau EINEN `role="status"`. */}
      {error && !sperre && events.length > 0 && (
        <Alert severity="error" variant="outlined" title={errorTitle || undefined}>
          The feed could not be refreshed: {error}. The rows below are the last
          successful load.
        </Alert>
      )}

      <Panel pad="none" data-testid="liste-panel">
        <Box sx={{ px: SP.card, pt: SP.card }}>
          <ListToolbar
            search={{
              value: begriff,
              onChange: (v) => set('q', v),
              onCommit: (v) => set('q', v),
              placeholder: 'Search events…',
              label: 'Search events',
              // G14a-3-Nachbesserung: die Rolle gehoert an den Aufrufer — die
              // Nachbarn (DocsIndex.jsx:129, JobsList.jsx:262) tragen sie
              // genauso. Ohne sie findet `SEL.suchfeld` (`[role="searchbox"]`)
              // nichts, und P5/P12/P23/P24/P16 melden ein leeres Z1.
              inputRole: 'searchbox',
              disabled: sperre,
              // 🔴 N2.9: die Suche fuellt den Rest von Z1. `ListToolbar` setzt
              // `flex: 1` am KASTEN; `SearchTrigger` reicht das `sx` an seine
              // Wurzel weiter — ohne diese Zeile stuende das Feld auf seiner
              // Eigenbreite (P24).
              sx: { flex: 1, minWidth: 0 },
            }}
            moreFilters={{
              // BS6 ist das Opt-in dieser Ansicht (§2.1): Desktop oeffnet ein
              // nicht-modales Popover, das Telefon behaelt das Blatt.
              criteria: kriterien,
              // „Show N results": `null` = die Zahl ist unbekannt — dann steht
              // „Show results" ohne Zahl (`FilterSheet.jsx:293-297`). Drei Lagen
              // tragen sie nicht: der gescheiterte Abruf (`undefined` = kein
              // Knopf), der GEKAPPTE Bestand (R15 W3: „Show 1260 results" waere
              // genau die exakte Summe, die der Zaehler daneben als „50+"
              // verweigert) und die Zaehlung `unbekannt` — dort stand im Blatt
              // „Show 0 results", waehrend der Zaehler „—" zeigte (R15 W1).
              resultCount: leerGrund === 'error' ? undefined : (kappt || zaehlungUnbekannt ? null : gesamt),
              activeCount: aktiveBlattkriterien,
              onClearAll: clearAll,
              disabled: sperre,
            }}
            count={leerGrund === 'error' ? undefined : {
              noun: 'events',
              // 🔴 Regel 22 §3: im `unknown`-Zustand ist die Zahl UNBEKANNT —
              // nicht die des letzten Erfolgslaufs. Das gilt auch im
              // Klemm-Fenster: dort ist die Antwort fuer die geklemmte Seite
              // noch unterwegs, und „0 of 3" waere die Zeile der alten Antwort.
              n: zaehlungUnbekannt || !jeGeladen ? null : events.length,
              m: zaehlungUnbekannt || !jeGeladen ? null : gesamt,
              // 🔴 B3: UNTERE Schranke. Eine Quelle ist bei 1000 gekappt, `total`
              // ist die Summe der gekappten Quellen — ab 1000 ist der Bestand
              // unbekannt, nie „gleich 1000".
              unknownTotal: !zaehlungUnbekannt && gesamt != null && gesamt >= QUELLEN_KAPPE,
              unknown: zaehlungUnbekannt,
              loading,
            }}
            refresh={<RefreshButton ariaLabel="Refresh the audit feed" onClick={refetch} />}
            query={{ chips, clearAll }}
          >
            {query.ignoredValues.length > 0 && (
              <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.tight }}>
                {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
              </Alert>
            )}

            {geruest && (
              <Box data-testid="liste-skelett" sx={{ px: SP.card, py: SP.card }}>
                <Stack spacing={SP.tight}>
                  {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}
                </Stack>
              </Box>
            )}

            {!geruest && (
              <Box
                data-testid="liste-karte"
                aria-busy={loading ? 'true' : 'false'}
                sx={{ px: SP.card, pt: SP.card }}
              >
                {leerGrund ? (
                  <Leerzustand
                    grund={leerGrund}
                    fehler={error}
                    begriff={begriff}
                    onRetry={wiederholen}
                    onClearSearch={() => set('q', null)}
                    onClearFilters={clearAll}
                  />
                ) : (
                  <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                    {events.map((e) => <EventRow key={e.id} event={e} />)}
                  </Stack>
                )}
              </Box>
            )}

            {/* Die Fusszeile steht NUR mit mehr als einer Seite (N8.7). */}
            {(gesamt ?? 0) > SEITE && !leerGrund && (
              <Box data-testid="liste-fuss">
                <TableFooterSimple
                  count={gesamt ?? 0}
                  // 🔴 R16-Runde 3, Hinweis 1: die GEKLEMMTE Seite, nicht die
                  // rohe Adresse — sonst druckt der erste Frame nach der Antwort
                  // eine verdrehte Spanne („151–120 of 120"). Dieselbe Grenze
                  // wie der Klemm-Effekt oben.
                  page={Math.min(seite, letzteSeite) - 1}
                  onPageChange={(p) => set('page', p + 1)}
                  rowsPerPage={SEITE}
                  // 🔴 R16 B2: ab der Kappe nennt die Fusszeile nur den BEREICH
                  // („1–50"), nie „1–50 of 1260". `count` ist dann die Summe der
                  // je Quelle gekappten Quellen — eine untere Schranke, und der
                  // Zaehler daneben sagt genau das („50+ events"). Die
                  // Blaettern-Navigation bleibt: `count` traegt sie weiter.
                  // Unterhalb der Kappe bleibt es bei MUI's Vorgabe („of N").
                  labelDisplayedRows={kappt ? ({ from, to }) => `${from}–${to}` : undefined}
                />
              </Box>
            )}
          </ListToolbar>
        </Box>
      </Panel>
    </Stack>
  )
}

/**
 * Die vier Leerzustände an EINER Stelle.
 *
 * `no-match` traegt das Begriff-Echo UND „Clear all filters": das Echo nennt den
 * Begriff, „Clear all filters" raeumt jede Dimension ab (auch eine gesetzte
 * Quelle oder ein Zeitfenster — bei denen das Echo allein nicht hilft).
 */
function Leerzustand({ grund, fehler, begriff, onRetry, onClearSearch, onClearFilters }) {
  if (grund === 'error') {
    return (
      <EmptyState
        reason="error"
        message={`Events could not be loaded: ${fehler}`}
        onRetry={onRetry}
        showDuringOutage
      />
    )
  }
  if (grund === 'unknown') {
    return (
      <EmptyState
        reason="unknown"
        message="The feed could not be refreshed. Whether there are events is unknown."
        detail={fehler ? `Last error: ${fehler}` : undefined}
        onRetry={onRetry}
      />
    )
  }
  if (grund === 'no-match') {
    return (
      <EmptyState
        reason="no-match"
        noun="event"
        query={begriff || undefined}
        onClearSearch={begriff ? onClearSearch : undefined}
        message={begriff ? undefined : 'No events match the current filters.'}
        onClearFilters={onClearFilters}
      />
    )
  }
  return (
    <EmptyState
      reason="no-data"
      message="No events in this time range."
      detail="Events appear here as soon as a sync run, a webhook, an email or a setting change is recorded."
    />
  )
}
